import { Link, Outlet, useParams, useRouterState } from '@tanstack/react-router'

import { cn } from '../../components/cn'
import { t } from '../../copy'
import { T } from '../../copy/text'
import { useProject } from '../../app/queries'
import { isActivityPath } from '../activity/path'

/**
 * 项目详情布局：≥40px 第二行（面包屑 + 九页签，含问题反馈）+ 子路由 Outlet。
 * Soft Structuralism：页签 14.5px / 500–600，珊瑚下划线选中。
 *
 * 页签走真实路径，不用 hash。选中态只看 pathname。
 */

const PROJECT_TABS = [
  { copyId: 'nav.ptab.overview', to: '/projects/$projectId', exact: true },
  { copyId: 'nav.ptab.chat', to: '/projects/$projectId/conversations', exact: false },
  { copyId: 'bug_reports.title', to: '/projects/$projectId/bug-reports', exact: false },
  { copyId: 'nav.ptab.tasks', to: '/projects/$projectId/tasks', exact: false },
  { copyId: 'nav.ptab.env', to: '/projects/$projectId/environments', exact: false },
  { copyId: 'nav.ptab.resources', to: '/projects/$projectId/resources', exact: false },
  { copyId: 'nav.ptab.release', to: '/projects/$projectId/releases', exact: false },
  { copyId: 'nav.ptab.memo', to: '/projects/$projectId/memos', exact: false },
  { copyId: 'nav.ptab.activity', to: '/projects/$projectId/activity', exact: false },
] as const

export function ProjectShell() {
  const { projectId } = useParams({ strict: false }) as { projectId: string }
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const project = useProject(projectId)
  const searchStr = useRouterState({ select: (s) => s.location.searchStr })
  const frame = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr).get('frame')
  const name = frame === 'A05' ? t('activity.project.name.npcshop') : project.data?.name

  const activity = isActivityPath(pathname)

  return (
    <div className={cn('projects-theme flex min-h-0 min-w-0 flex-1 flex-col', activity ? 'bg-[var(--bg-0)] text-[var(--t1)]' : 'bg-npc-lulu-bg-0 text-npc-lulu-t1')}>
      {/* 桌面档没有顶栏：sticky 的 top 必须是 0。2026-09-05 生产：这里写着 top-[46px]（活动中心改版时按一个
          不存在的 46px 顶栏算的），于是 tab 条常年被顶下去 46px，把每个模块页的标题整个盖住、按钮盖一半——
          arron 那句「按钮挡住了」。手机档有 52px 的 mobile-header，那一档照旧。 */}
      <div
        data-testid="project-tab-bar"
        className={cn(
          'sticky top-0 z-20 flex h-[44px] w-full min-w-0 shrink-0 items-stretch overflow-hidden border-b px-npc-page-x',
          activity
            ? 'border-[var(--l1)] bg-[var(--bg-2)] whitespace-nowrap max-[600px]:hidden'
            : 'border-npc-lulu-line-1 bg-npc-lulu-bg-1 max-[600px]:top-[52px] max-[600px]:h-[48px] max-[600px]:items-center max-[600px]:px-npc-grid-sm',  /* npc-token-allow: Soft Structuralism secondary tabs row ≥40px */
        )}
      >
        <span data-testid="project-tab-breadcrumb" className={cn('flex max-w-[40vw] min-w-0 shrink-0 items-center overflow-hidden whitespace-nowrap text-[13px] font-medium', activity ? 'text-[var(--t3)]' : 'text-npc-lulu-t3')}>  {/* npc-token-allow: Soft Structuralism breadcrumb 13px hierarchy next to 14.5px tabs */}
          <Link to="/projects" activeOptions={{ exact: true }} className={activity ? 'shrink-0 text-[var(--t3)]' : 'shrink-0 text-npc-lulu-t3 hover:text-npc-lulu-t2'}>
            <T id="nav.project" />
          </Link>
          <span className={cn('mx-1.5 shrink-0', activity ? 'text-[var(--t4)]' : 'text-npc-lulu-t4')} aria-hidden>
            ›
          </span>
          <span className={cn('min-w-0 truncate font-medium', activity ? 'text-[var(--t1)]' : 'text-npc-lulu-t2')}>{name ?? t('nav.project.loading')}</span>
        </span>

        <nav
          aria-label={t('nav.project_tabs')}
          className="relative flex min-w-0 flex-1 items-center gap-1 overflow-x-auto pl-3"  /* npc-token-allow: Soft Structuralism tab breathing room */
        >
          {PROJECT_TABS.map((tab) => {
            const href = tab.to.replace('$projectId', projectId)
            const active = tab.exact
              ? pathname === href || pathname === `${href}/`
              : pathname === href || pathname.startsWith(`${href}/`)
            return (
              <Link
                key={tab.copyId}
                to={tab.to}
                params={{ projectId }}
                activeOptions={{ exact: tab.exact }}
                aria-current={active ? 'page' : undefined}
                data-label={t(tab.copyId)}
                className={cn(
                  'pj-tab-stable-label grid h-full shrink-0 place-items-center whitespace-nowrap border-b-[3px] px-3.5 text-[14.5px] tracking-[-0.01em] transition-colors',  /* npc-token-allow: Soft Structuralism secondary tab 14.5px + stable tracking */
                  activity
                    ? cn(
                        'no-underline',
                        active ? 'border-b-[var(--accent)] text-[var(--t1)]' : 'border-transparent text-[var(--t3)] hover:text-[var(--t1)]',
                      )
                    : cn(
                        // Soft Structuralism：14.5px / 500–600，行高 ≥40，珊瑚加粗下划线。移动：药丸 chips 横滑
                        'pj-tab',
                        'max-[600px]:h-[32px] max-[600px]:rounded-[8px] max-[600px]:border max-[600px]:border-b max-[600px]:px-[12px] max-[600px]:text-npc-body-sm',  /* npc-token-allow: mobile chip size from MobileNav reference */
                        active
                          ? 'pj-tab-active border-b-[3px] max-[600px]:bg-[var(--pj-accent-soft)] max-[600px]:border-[var(--pj-accent-border)] max-[600px]:text-[var(--pj-accent)]'
                          : 'border-transparent text-npc-lulu-t2 hover:text-npc-lulu-t1 max-[600px]:bg-npc-lulu-bg-1 max-[600px]:border-npc-lulu-line-1',
                      ),
                )}
              >
                <span className={cn('col-start-1 row-start-1', active ? 'font-semibold' : 'font-medium')}><T id={tab.copyId} /></span>
              </Link>
            )
          })}
        </nav>
          {/* 移动端右缘渐隐（MobileNav 参考）：提示可横滑 */}
          <span
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 hidden h-full w-[24px] bg-gradient-to-r from-transparent to-npc-lulu-bg-1 max-[600px]:block"
          />
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Outlet />
      </div>
    </div>
  )
}
